/**
 * @module OrganizationContext
 * @description Contexto global para la gestión de la jerarquía de organizaciones del MINSAL.
 *
 * Implementa el modelo de tres niveles del Ministerio de Salud de Chile:
 *   - Nivel 1: Ministerio de Salud (MINSAL) — visibilidad total
 *   - Nivel 2: Servicios de Salud (29), SEREMIS (15), Organismos Autónomos (Fonasa, ISP, etc.)
 *   - Nivel 3: Hospitales alta/media/baja complejidad, CESFAM, SAP, etc.
 *
 * El acceso jerárquico sigue el principio de "worst-case bubbling": el cumplimiento de
 * un nodo padre refleja el estado más crítico de sus hijos. Un Servicio de Salud
 * con un hospital al 44% de cumplimiento verá ese valor reflejado en su dashboard.
 *
 * ## Fuentes de datos
 * Las métricas se obtienen de vistas materializadas que se refrescan periódicamente:
 *   - `mv_compliance_stats`    — cumplimiento por organización × framework
 *   - `mv_regional_compliance` — agregación por región chilena × framework
 *   - `mv_national_compliance` — métricas nacionales para el dashboard del Ministro/CISO
 *   - `mv_critical_alerts`     — alertas escaladas con severidad y días de atraso
 *
 * Si las vistas no existen (instancias sin jerarquía MINSAL), el contexto cae a datos
 * mock pre-cargados para que el mapa y los dashboards sigan funcionando sin errores.
 *
 * @see supabase/migrations/20251123000000_hierarchical_organizations.sql
 * @see supabase/migrations/20251123100000_materialized_views.sql
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import { useAuth } from './AuthContext';
import type { Organization, OrganizationType, ChileRegion } from '../types/iso';

// =====================================================
// TIPOS E INTERFACES
// =====================================================

/**
 * Nodo en el árbol de organizaciones con referencias explícitas padre-hijos.
 * Extiende `Organization` con metadatos de árbol calculados en tiempo de construcción.
 *
 * @property children   - Hijos directos en la jerarquía (undefined si es hoja)
 * @property parent     - Referencia al nodo padre (undefined en la raíz MINSAL)
 * @property childCount - Cantidad de hijos directos (shortcut para no hacer children.length)
 */
export interface OrganizationNode extends Organization {
  children?: OrganizationNode[];
  parent?: OrganizationNode;
  childCount?: number;
}

/**
 * Estadísticas de cumplimiento de una organización para un framework normativo dado.
 * Proviene de la vista materializada `mv_compliance_stats`.
 *
 * @property id                           - UUID de la organización
 * @property frameworkId                  - UUID del framework (ISO 27002, LMCS, CIS, etc.)
 * @property compliancePercentage         - Porcentaje global (0-100)
 * @property criticalCompliancePercentage - Porcentaje solo para controles marcados como críticos
 * @property criticalControlsFailed       - Cantidad de controles críticos en estado no_aplica/pendiente/vencido
 * @property overdueTasks                 - Tareas con fecha_límite en el pasado y estado no completado
 * @property complianceColor              - Color semáforo calculado (≥90=green, ≥70=yellow, ≥50=orange, <50=red)
 * @property complianceStatus             - Etiqueta legible del semáforo (Excelente/Bueno/Regular/Crítico)
 */
export interface OrganizationStats {
  id: string;
  name: string;
  orgType: OrganizationType;
  level: number;
  regionCode?: ChileRegion;
  frameworkId: string;
  frameworkCode: string;
  totalControls: number;
  completedControls: number;
  compliancePercentage: number;
  criticalCompliancePercentage: number;
  criticalControlsFailed: number;
  overdueTasks: number;
  complianceColor: 'green' | 'yellow' | 'orange' | 'red';
  complianceStatus: string;
}

/**
 * Estadísticas de cumplimiento agregadas por región y framework.
 * Proviene de `mv_regional_compliance`. Usada por el mapa interactivo `ChileMap.tsx`.
 *
 * @property regionCode              - Código de región chilena (XV, I, II … XII, RM, XVI, XIV)
 * @property avgCompliancePercentage - Promedio simple de cumplimiento de todas las orgs de la región
 * @property worstCaseCompliance     - El valor más bajo de la región (worst-case para alertar)
 * @property bestCaseCompliance      - El valor más alto (opcional, no siempre calculado)
 * @property orgsBelowThreshold      - Organizaciones con cumplimiento < 70% (umbral regulatorio)
 * @property totalCriticalFailed     - Suma de controles críticos fallidos en toda la región
 */
export interface RegionalStats {
  regionCode: ChileRegion;
  frameworkId: string;
  totalOrganizations: number;
  avgCompliancePercentage: number;
  worstCaseCompliance: number;
  bestCaseCompliance?: number;
  orgsBelowThreshold: number;
  totalCriticalFailed: number;
}

/**
 * Métricas nacionales consolidadas para el dashboard del Ministro / CISO Nacional.
 * Proviene de `mv_national_compliance`. Una fila por framework activo.
 *
 * @property nationalAvgCompliance - Promedio ponderado o simple de todas las 289+ organizaciones
 * @property orgsExcellent         - Cantidad con ≥90% (verde)
 * @property orgsGood              - Cantidad con 70-89% (amarillo)
 * @property orgsRegular           - Cantidad con 50-69% (naranja)
 * @property orgsCritical          - Cantidad con <50% (rojo) — requieren intervención inmediata
 * @property criticalAlertsCount   - Alertas activas en `mv_critical_alerts` con severidad critical/high
 * @property worstCompliance       - El valor más bajo de todo el país (determina el estado general)
 */
export interface NationalStats {
  frameworkId: string;
  frameworkCode: string;
  frameworkName: string;
  totalOrganizations: number;
  nationalAvgCompliance: number;
  orgsExcellent: number;
  orgsGood: number;
  orgsRegular: number;
  orgsCritical: number;
  criticalAlertsCount: number;
  worstCompliance: number;
}

/**
 * Alerta crítica generada por el mecanismo de Risk Bubbling.
 * Proviene de `mv_critical_alerts`. Aparece en el panel `CriticalAlertsPanel.tsx`.
 *
 * El trigger `generate_critical_alert()` en PostgreSQL detecta cuando un control
 * crítico falla en una organización crítica y escala la alerta a todos sus ancestros.
 *
 * @property alertSeverity - 'critical' = control crítico fallido + org crítica + > 30 días vencido
 * @property alertType     - Categoría: 'overdue_critical', 'compliance_drop', 'evidence_missing', etc.
 * @property daysOverdue   - Días desde que la fecha límite del control fue superada
 * @property detectedAt    - Timestamp ISO 8601 de cuándo el trigger generó la alerta
 */
export interface CriticalAlert {
  id: string;
  organizationId: string;
  organizationName: string;
  orgType: OrganizationType;
  regionCode?: ChileRegion;
  parentId?: string;
  parentName?: string;
  frameworkId: string;
  frameworkCode: string;
  controlId: string;
  controlCode: string;
  controlTitle: string;
  controlStatus: string;
  dueDate?: string;
  alertSeverity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  alertType: string;
  daysOverdue: number;
  detectedAt: string;
}

/**
 * Contrato del contexto expuesto a los componentes consumidores.
 * Separado en cuatro grupos lógicos para claridad en el IntelliSense.
 */
interface OrganizationContextType {
  // Estado
  currentOrganization: Organization | null;
  organizations: Organization[];
  organizationTree: OrganizationNode | null;
  /** Hijos directos de `currentOrganization` — calculado derivado, no estado separado */
  childOrganizations: Organization[];
  /** Padre de `currentOrganization` — null si es la raíz (MINSAL) */
  parentOrganization: Organization | null;
  isLoading: boolean;
  error: string | null;
  isDemoMode: boolean;

  // Métricas (de vistas materializadas)
  organizationStats: OrganizationStats[];
  regionalStats: RegionalStats[];
  nationalStats: NationalStats | null;
  criticalAlerts: CriticalAlert[];

  // Funciones de navegación
  getOrganization: (id: string) => Organization | undefined;
  getChildren: (orgId: string) => Organization[];
  getParent: (orgId: string) => Organization | undefined;
  getAncestors: (orgId: string) => Organization[];
  getDescendants: (orgId: string) => Organization[];
  canAccessOrganization: (targetOrgId: string) => boolean;

  // Funciones de datos
  refreshOrganizations: () => Promise<void>;
  refreshStats: (frameworkId?: string) => Promise<void>;
  getOrgStatsByFramework: (orgId: string, frameworkId: string) => OrganizationStats | undefined;
  getRegionalStatsByFramework: (regionCode: ChileRegion, frameworkId: string) => RegionalStats | undefined;

  // Funciones de filtrado
  getOrganizationsByType: (type: OrganizationType) => Organization[];
  getOrganizationsByRegion: (region: ChileRegion) => Organization[];
  getOrganizationsByLevel: (level: number) => Organization[];
}

// =====================================================
// DATOS MOCK
// =====================================================

/**
 * Conjunto mínimo de organizaciones para el modo demo.
 * Representa una jerarquía válida de 3 niveles: MINSAL → SS → Hospital.
 * Los IDs con prefijos a/b/c y sufijos numéricos son estables entre reinicios del demo.
 *
 * Sirve también como fallback cuando la tabla `organizations` está vacía o inaccesible,
 * garantizando que el árbol y los dashboards jerárquicos nunca queden en blanco.
 */
const MOCK_ORGANIZATIONS: Organization[] = [
  {
    id: 'a0000000-0000-0000-0000-000000000001',
    name: 'Ministerio de Salud de Chile',
    slug: 'minsal',
    orgType: 'ministry',
    level: 1,
    parentId: undefined,
    regionCode: undefined,
    isCritical: true,
    isActive: true,
    bedCount: 0,
    populationServed: 19000000,
    description: 'Ministerio de Salud - Nivel Central'
  },
  {
    id: 'b0000000-0000-0000-0000-000000000001',
    name: 'Servicio de Salud Metropolitano Occidente',
    slug: 'ssmo',
    orgType: 'health_service',
    level: 2,
    parentId: 'a0000000-0000-0000-0000-000000000001',
    regionCode: 'RM',
    isCritical: true,
    isActive: true,
    bedCount: 0,
    populationServed: 1500000,
    description: 'Servicio de Salud del sector poniente de Santiago'
  },
  {
    id: 'c0000000-0000-0000-0000-000000000001',
    name: 'Hospital San Juan de Dios',
    slug: 'hsjd',
    orgType: 'hospital_high_complexity',
    level: 3,
    parentId: 'b0000000-0000-0000-0000-000000000001',
    regionCode: 'RM',
    isCritical: true,
    isActive: true,
    bedCount: 500,
    populationServed: 300000,
    description: 'Hospital de Alta Complejidad'
  },
  {
    id: 'b0000000-0000-0000-0000-000000000002',
    name: 'Servicio de Salud Metropolitano Norte',
    slug: 'ssmn',
    orgType: 'health_service',
    level: 2,
    parentId: 'a0000000-0000-0000-0000-000000000001',
    regionCode: 'RM',
    isCritical: true,
    isActive: true,
    bedCount: 0,
    populationServed: 1200000
  },
  {
    id: 'c0000000-0000-0000-0000-000000000002',
    name: 'Hospital Roberto del Río',
    slug: 'hrdr',
    orgType: 'hospital_high_complexity',
    level: 3,
    parentId: 'b0000000-0000-0000-0000-000000000002',
    regionCode: 'RM',
    isCritical: true,
    isActive: true,
    bedCount: 300,
    populationServed: 200000
  },
  {
    id: 'b0000000-0000-0000-0000-000000000003',
    name: 'Fondo Nacional de Salud',
    slug: 'fonasa',
    orgType: 'autonomous_entity',
    level: 2,
    parentId: 'a0000000-0000-0000-0000-000000000001',
    regionCode: undefined,
    isCritical: true,
    isActive: true,
    bedCount: 0,
    populationServed: 15000000
  }
];

/**
 * Estadísticas regionales mock para las 16 regiones de Chile.
 * Valores representativos (no reales) usados para el mapa interactivo en modo demo.
 *
 * La Región Metropolitana y Valparaíso tienen el mayor número de organizaciones
 * y los mejores índices de cumplimiento (reflejo de mayor capacidad instalada).
 * La Región de Aysén (XI) y La Araucanía (IX) tienen los peores (aislamiento, recursos).
 *
 * Se inicializa directamente en el estado del contexto para que el mapa se muestre
 * inmediatamente sin esperar a que `loadStats()` retorne — mejora la UX de carga.
 */
const MOCK_REGIONAL_STATS: RegionalStats[] = [
  {
    regionCode: 'XV',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 8,
    avgCompliancePercentage: 78.5,
    worstCaseCompliance: 65.2,
    bestCaseCompliance: 92.3,
    orgsBelowThreshold: 1,
    totalCriticalFailed: 3
  },
  {
    regionCode: 'I',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 12,
    avgCompliancePercentage: 72.3,
    worstCaseCompliance: 58.1,
    bestCaseCompliance: 88.7,
    orgsBelowThreshold: 2,
    totalCriticalFailed: 5
  },
  {
    regionCode: 'II',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 14,
    avgCompliancePercentage: 82.7,
    worstCaseCompliance: 71.5,
    bestCaseCompliance: 95.1,
    orgsBelowThreshold: 0,
    totalCriticalFailed: 2
  },
  {
    regionCode: 'III',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 10,
    avgCompliancePercentage: 68.4,
    worstCaseCompliance: 52.3,
    bestCaseCompliance: 84.6,
    orgsBelowThreshold: 3,
    totalCriticalFailed: 8
  },
  {
    regionCode: 'IV',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 16,
    avgCompliancePercentage: 75.1,
    worstCaseCompliance: 63.8,
    bestCaseCompliance: 89.4,
    orgsBelowThreshold: 1,
    totalCriticalFailed: 4
  },
  {
    regionCode: 'V',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 28,
    avgCompliancePercentage: 88.9,
    worstCaseCompliance: 79.2,
    bestCaseCompliance: 97.8,
    orgsBelowThreshold: 0,
    totalCriticalFailed: 1
  },
  {
    regionCode: 'RM',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 45,
    avgCompliancePercentage: 91.2,
    worstCaseCompliance: 82.5,
    bestCaseCompliance: 98.6,
    orgsBelowThreshold: 0,
    totalCriticalFailed: 0
  },
  {
    regionCode: 'VI',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 18,
    avgCompliancePercentage: 73.6,
    worstCaseCompliance: 61.4,
    bestCaseCompliance: 87.9,
    orgsBelowThreshold: 2,
    totalCriticalFailed: 6
  },
  {
    regionCode: 'VII',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 22,
    avgCompliancePercentage: 79.8,
    worstCaseCompliance: 68.9,
    bestCaseCompliance: 93.2,
    orgsBelowThreshold: 1,
    totalCriticalFailed: 3
  },
  {
    regionCode: 'XVI',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 9,
    avgCompliancePercentage: 71.2,
    worstCaseCompliance: 59.7,
    bestCaseCompliance: 85.8,
    orgsBelowThreshold: 2,
    totalCriticalFailed: 7
  },
  {
    regionCode: 'VIII',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 35,
    avgCompliancePercentage: 85.4,
    worstCaseCompliance: 74.3,
    bestCaseCompliance: 96.7,
    orgsBelowThreshold: 0,
    totalCriticalFailed: 2
  },
  {
    regionCode: 'IX',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 19,
    avgCompliancePercentage: 67.8,
    worstCaseCompliance: 48.2,
    bestCaseCompliance: 81.5,
    orgsBelowThreshold: 4,
    totalCriticalFailed: 12
  },
  {
    regionCode: 'XIV',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 11,
    avgCompliancePercentage: 76.5,
    worstCaseCompliance: 65.1,
    bestCaseCompliance: 90.3,
    orgsBelowThreshold: 1,
    totalCriticalFailed: 4
  },
  {
    regionCode: 'X',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 26,
    avgCompliancePercentage: 81.3,
    worstCaseCompliance: 70.8,
    bestCaseCompliance: 94.5,
    orgsBelowThreshold: 1,
    totalCriticalFailed: 3
  },
  {
    regionCode: 'XI',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 7,
    avgCompliancePercentage: 63.2,
    worstCaseCompliance: 44.5,
    bestCaseCompliance: 78.9,
    orgsBelowThreshold: 3,
    totalCriticalFailed: 9
  },
  {
    regionCode: 'XII',
    frameworkId: 'fw-iso27002',
    totalOrganizations: 9,
    avgCompliancePercentage: 69.7,
    worstCaseCompliance: 55.3,
    bestCaseCompliance: 83.1,
    orgsBelowThreshold: 2,
    totalCriticalFailed: 7
  }
];

/**
 * Estadísticas nacionales mock representando el estado del sistema MINSAL completo.
 * 289 organizaciones, 76.8% de cumplimiento promedio — valores de referencia del diseño.
 *
 * Este objeto se pre-carga en el estado inicial para que el `NationalDashboard` tenga
 * datos visibles mientras `loadStats()` realiza la consulta real a `mv_national_compliance`.
 */
const MOCK_NATIONAL_STATS: NationalStats = {
  frameworkId: 'fw-iso27002',
  frameworkCode: 'ISO27002',
  frameworkName: 'ISO/IEC 27002:2022',
  totalOrganizations: 289,
  nationalAvgCompliance: 76.8,
  orgsExcellent: 48,
  orgsGood: 156,
  orgsRegular: 67,
  orgsCritical: 18,
  criticalAlertsCount: 12,
  worstCompliance: 44.5
};

// =====================================================
// CONTEXTO
// =====================================================

/** Contexto React interno — consumir mediante `useOrganization()`, no directamente. */
const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

// =====================================================
// PROVIDER
// =====================================================

interface OrganizationProviderProps {
  children: React.ReactNode;
}

/**
 * Proveedor del contexto de organizaciones. Debe envolver toda la aplicación.
 * Espera a que `dbUserReady` sea `true` antes de iniciar la carga de datos para
 * garantizar que el `user.organization_id` ya fue validado en PostgreSQL.
 *
 * @param props.children - Árbol de componentes React que consumen el contexto
 */
export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const { user, dbUserReady } = useAuth();

  // Estado principal — lista plana + árbol construido en memoria
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [organizationTree, setOrganizationTree] = useState<OrganizationNode | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  // Métricas — inicializadas con datos mock para visualización inmediata del mapa y KPIs
  const [organizationStats, setOrganizationStats] = useState<OrganizationStats[]>([]);
  const [regionalStats, setRegionalStats] = useState<RegionalStats[]>(MOCK_REGIONAL_STATS);
  const [nationalStats, setNationalStats] = useState<NationalStats | null>(MOCK_NATIONAL_STATS);
  const [criticalAlerts, setCriticalAlerts] = useState<CriticalAlert[]>([]);

  // =====================================================
  // FUNCIONES DE CARGA
  // =====================================================

  /**
   * Carga todas las organizaciones activas desde la tabla `organizations` de Supabase.
   * Transforma el formato snake_case de la BD al camelCase de la aplicación.
   * Si la carga falla (sin conexión, tabla vacía), cae a MOCK_ORGANIZATIONS automáticamente.
   *
   * Tras cargar, identifica la organización del usuario autenticado usando
   * `user.organization_id` y construye el árbol jerárquico en memoria.
   */
  const loadOrganizations = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      logger.info('[ORG] Cargando organizaciones...');

      const { data, error: fetchError } = await supabase
        .from('organizations')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (fetchError) throw fetchError;

      if (data && data.length > 0) {
        // Transformar de formato BD (snake_case) a formato de aplicación (camelCase)
        const transformed: Organization[] = data.map((org) => ({
          id: org.id,
          name: org.name,
          slug: org.slug,
          orgType: org.org_type as OrganizationType,
          level: org.level,
          parentId: org.parent_id,
          regionCode: org.region_code as ChileRegion | undefined,
          isCritical: org.is_critical,
          isActive: org.is_active,
          bedCount: org.bed_count,
          populationServed: org.population_served,
          deisCode: org.deis_code,
          description: org.description,
          settings: org.settings,
          storageProvider: org.storage_provider || 'supabase',
          isCustomStorageEnabled: org.is_custom_storage_enabled || false
        }));

        setOrganizations(transformed);
        setIsDemoMode(false);

        // Buscar la organización del usuario autenticado para establecer el contexto de navegación
        const currentOrg = transformed.find(o => o.id === user.organization_id);
        setCurrentOrganization(currentOrg || null);

        // Construir árbol para funciones de navegación (getDescendants, canAccessOrganization, etc.)
        const tree = buildOrganizationTree(transformed);
        setOrganizationTree(tree);

        logger.info(`[ORG] ${transformed.length} organizaciones cargadas`);
      } else {
        throw new Error('No se encontraron organizaciones');
      }
    } catch (err) {
      // Sin conexión o tabla vacía — modo demo con datos representativos del MINSAL
      logger.warn('[ORG] Error cargando desde BD, usando modo demo:', err);
      setOrganizations(MOCK_ORGANIZATIONS);
      setIsDemoMode(true);

      // En demo: buscar primero la org del usuario, si no existe usar MINSAL como raíz
      const currentOrg = MOCK_ORGANIZATIONS.find(o => o.id === user.organization_id);
      setCurrentOrganization(currentOrg || MOCK_ORGANIZATIONS[0]);

      const tree = buildOrganizationTree(MOCK_ORGANIZATIONS);
      setOrganizationTree(tree);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  /**
   * Carga métricas de cumplimiento desde las cuatro vistas materializadas del sistema.
   * Cada vista es opcional — si no existe (instancia sin módulo jerárquico MINSAL),
   * se registra como "información" y se omite sin afectar otras cargas.
   *
   * Este comportamiento permisivo permite desplegar el sistema tanto en organizaciones
   * individuales (sin jerarquía) como en la configuración completa MINSAL.
   *
   * @param frameworkId - Filtrar resultados por framework específico (opcional).
   *                      Si se omite, carga todas las filas disponibles.
   */
  const loadStats = useCallback(async (frameworkId?: string) => {
    try {
      logger.info('[ORG] Cargando estadísticas...');

      // ── 1. Estadísticas por organización (mv_compliance_stats) ──────────────
      let statsQuery = supabase
        .from('mv_compliance_stats')
        .select('*');

      if (frameworkId) {
        statsQuery = statsQuery.eq('framework_id', frameworkId);
      }

      const { data: statsData, error: statsError } = await statsQuery;

      if (!statsError && statsData) {
        const transformedStats: OrganizationStats[] = statsData.map((s) => ({
          id: s.organization_id,
          name: s.organization_name,
          orgType: s.org_type as OrganizationType,
          level: s.org_level,
          regionCode: s.region_code as ChileRegion | undefined,
          frameworkId: s.framework_id,
          frameworkCode: s.framework_code,
          totalControls: s.total_controls,
          completedControls: s.completed_controls,
          compliancePercentage: s.compliance_percentage,
          criticalCompliancePercentage: s.critical_compliance_percentage,
          criticalControlsFailed: s.critical_controls_failed,
          overdueTasks: s.overdue_tasks,
          // Calcular color y estado del semáforo en el cliente para evitar lógica en la vista
          complianceColor: getComplianceColor(s.compliance_percentage),
          complianceStatus: getComplianceStatus(s.compliance_percentage)
        }));
        setOrganizationStats(transformedStats);
      } else if (statsError) {
        // La vista no existe — normal en configuraciones sin módulo jerárquico MINSAL
        logger.info('[ORG] Vista mv_compliance_stats no disponible (esperado si no se usa jerarquía MINSAL)');
        setOrganizationStats([]);
      }

      // ── 2. Estadísticas regionales para el mapa (mv_regional_compliance) ───
      let regionalQuery = supabase
        .from('mv_regional_compliance')
        .select('*');

      if (frameworkId) {
        regionalQuery = regionalQuery.eq('framework_id', frameworkId);
      }

      const { data: regionalData, error: regionalError } = await regionalQuery;

      if (!regionalError && regionalData) {
        const transformedRegional: RegionalStats[] = regionalData.map((r) => ({
          regionCode: r.region_code as ChileRegion,
          frameworkId: r.framework_id,
          totalOrganizations: r.total_organizations,
          avgCompliancePercentage: r.avg_compliance_percentage,
          worstCaseCompliance: r.worst_case_compliance,
          orgsBelowThreshold: r.orgs_below_threshold,
          totalCriticalFailed: r.total_critical_failed
        }));
        setRegionalStats(transformedRegional);
      } else if (regionalError) {
        logger.info('[ORG] Vista mv_regional_compliance no disponible (esperado si no se usa jerarquía MINSAL)');
        // Mantener los MOCK_REGIONAL_STATS del estado inicial para que el mapa siga visible
        setRegionalStats([]);
      }

      // ── 3. Estadísticas nacionales para el dashboard del Ministro (mv_national_compliance) ──
      let nationalQuery = supabase
        .from('mv_national_compliance')
        .select('*');

      // La vista devuelve una fila por framework — usar .single() para obtener objeto directo
      if (frameworkId) {
        nationalQuery = nationalQuery.eq('framework_id', frameworkId).single();
      } else {
        nationalQuery = nationalQuery.limit(1).single();
      }

      const { data: nationalData, error: nationalError } = await nationalQuery;

      if (!nationalError && nationalData) {
        setNationalStats({
          frameworkId: nationalData.framework_id,
          frameworkCode: nationalData.framework_code,
          frameworkName: nationalData.framework_name,
          totalOrganizations: nationalData.total_organizations,
          nationalAvgCompliance: nationalData.national_avg_compliance,
          orgsExcellent: nationalData.orgs_excellent,
          orgsGood: nationalData.orgs_good,
          orgsRegular: nationalData.orgs_regular,
          orgsCritical: nationalData.orgs_critical,
          criticalAlertsCount: nationalData.critical_alerts_count,
          worstCompliance: nationalData.worst_compliance
        });
      } else if (nationalError) {
        logger.info('[ORG] Vista mv_national_compliance no disponible (esperado si no se usa jerarquía MINSAL)');
        setNationalStats(null);
      }

      // ── 4. Alertas críticas (mv_critical_alerts) — Risk Bubbling activo ────
      // Limitado a 50 alertas para el panel lateral; ordenadas por severidad descendente
      // para que las más críticas aparezcan primero sin necesidad de re-ordenar en el cliente
      const { data: alertsData, error: alertsError } = await supabase
        .from('mv_critical_alerts')
        .select('*')
        .order('alert_severity', { ascending: false })
        .limit(50);

      if (!alertsError && alertsData) {
        const transformedAlerts: CriticalAlert[] = alertsData.map((a) => ({
          // ID compuesto organización+control — la alerta es única por esta combinación
          id: `${a.organization_id}-${a.control_id}`,
          organizationId: a.organization_id,
          organizationName: a.organization_name,
          orgType: a.org_type as OrganizationType,
          regionCode: a.region_code as ChileRegion | undefined,
          parentId: a.parent_id,
          parentName: a.parent_name,
          frameworkId: a.framework_id,
          frameworkCode: a.framework_code,
          controlId: a.control_id,
          controlCode: a.control_code,
          controlTitle: a.control_title,
          controlStatus: a.control_status,
          dueDate: a.due_date,
          alertSeverity: a.alert_severity as CriticalAlert['alertSeverity'],
          alertType: a.alert_type,
          daysOverdue: a.days_overdue,
          detectedAt: a.detected_at
        }));
        setCriticalAlerts(transformedAlerts);
      } else if (alertsError) {
        // Vista no existe o sin permisos — normal en configuraciones sin jerarquía
        logger.info('[ORG] Vista mv_critical_alerts no disponible (esperado si no se usa jerarquía MINSAL)');
        setCriticalAlerts([]);
      }

      logger.info('[ORG] Estadísticas cargadas');
    } catch (err) {
      // Error inesperado (no relacionado con vistas faltantes) — usar datos mock como fallback
      logger.warn('[ORG] Error cargando estadísticas, usando datos mock:', err);
      setRegionalStats(MOCK_REGIONAL_STATS);
      setNationalStats(MOCK_NATIONAL_STATS);
      setOrganizationStats([]);
      setCriticalAlerts([]);
    }
  }, []);

  // =====================================================
  // EFECTOS
  // =====================================================

  /**
   * Dispara `loadOrganizations` cuando el usuario está autenticado y su registro
   * en `public.users` fue verificado. Ambas condiciones son necesarias porque
   * `user.organization_id` proviene de PostgreSQL, no del JWT de Supabase Auth.
   */
  useEffect(() => {
    if (user?.organization_id && dbUserReady) {
      loadOrganizations();
    }
  }, [user?.organization_id, dbUserReady, loadOrganizations]);

  /**
   * Carga estadísticas solo después de tener las organizaciones.
   * La dependencia `organizations.length` evita ejecutar la carga antes de que
   * `loadOrganizations` haya completado y poblado el array.
   */
  useEffect(() => {
    if (user?.organization_id && dbUserReady && organizations.length > 0) {
      loadStats();
    }
  }, [user?.organization_id, dbUserReady, organizations.length, loadStats]);

  // =====================================================
  // FUNCIONES HELPER
  // =====================================================

  /**
   * Construye un árbol de nodos enlazados a partir de la lista plana de organizaciones.
   * Usa un Map para lograr complejidad O(n) en lugar de O(n²) con búsquedas anidadas.
   *
   * Algoritmo:
   *   1. Crear nodos con arrays `children` vacíos para todas las organizaciones
   *   2. Recorrer cada nodo; si tiene `parentId`, agregar la referencia al nodo padre
   *   3. El nodo sin `parentId` es la raíz (MINSAL en la jerarquía MINSAL)
   *
   * @param orgs - Lista plana de organizaciones con sus relaciones `parentId`
   * @returns Nodo raíz con árbol completo enlazado, o null si la lista está vacía
   */
  const buildOrganizationTree = (orgs: Organization[]): OrganizationNode | null => {
    const orgMap = new Map<string, OrganizationNode>();

    // Primera pasada: crear todos los nodos con referencias vacías
    orgs.forEach(org => {
      orgMap.set(org.id, { ...org, children: [], childCount: 0 });
    });

    // Segunda pasada: establecer relaciones padre-hijo y detectar la raíz
    let root: OrganizationNode | null = null;

    orgs.forEach(org => {
      const node = orgMap.get(org.id)!;

      if (org.parentId) {
        const parent = orgMap.get(org.parentId);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(node);
          parent.childCount = (parent.childCount || 0) + 1;
          // Referencia bidireccional para navegación ascendente desde el nodo
          node.parent = parent;
        }
      } else {
        // Nodo sin padre = raíz del árbol
        root = node;
      }
    });

    return root;
  };

  /**
   * Convierte un porcentaje de cumplimiento en el color del semáforo estándar.
   * Umbrales definidos por la política de cumplimiento del MINSAL:
   *   - Verde (≥90%)  — Excelente: dentro del objetivo regulatorio
   *   - Amarillo (≥70%) — Bueno: en observación, mejorable
   *   - Naranja (≥50%) — Regular: requiere plan de mejora documentado
   *   - Rojo (<50%)  — Crítico: requiere intervención inmediata y reporte al CISO
   */
  const getComplianceColor = (percentage: number): 'green' | 'yellow' | 'orange' | 'red' => {
    if (percentage >= 90) return 'green';
    if (percentage >= 70) return 'yellow';
    if (percentage >= 50) return 'orange';
    return 'red';
  };

  /**
   * Devuelve la etiqueta textual del semáforo de cumplimiento.
   * Los umbrales son idénticos a `getComplianceColor` para garantizar consistencia
   * entre el color visual y el texto mostrado al usuario.
   */
  const getComplianceStatus = (percentage: number): string => {
    if (percentage >= 90) return 'Excelente';
    if (percentage >= 70) return 'Bueno';
    if (percentage >= 50) return 'Regular';
    return 'Crítico';
  };

  // =====================================================
  // FUNCIONES DE NAVEGACIÓN
  // =====================================================

  /**
   * Busca una organización por su ID en la lista plana.
   * O(n) — para uso puntual. Si se necesita acceso frecuente, considerar un Map derivado.
   *
   * @param id - UUID de la organización
   * @returns La organización si existe, `undefined` si no
   */
  const getOrganization = useCallback((id: string): Organization | undefined => {
    return organizations.find(o => o.id === id);
  }, [organizations]);

  /**
   * Retorna los hijos directos de una organización (nivel inmediatamente inferior).
   * Ejemplo: getChildren(MINSAL.id) → [SS Metropolitano Norte, SS Occidente, Fonasa, ...]
   *
   * @param orgId - UUID de la organización padre
   * @returns Array de organizaciones hijas (vacío si es hoja)
   */
  const getChildren = useCallback((orgId: string): Organization[] => {
    return organizations.filter(o => o.parentId === orgId);
  }, [organizations]);

  /**
   * Retorna el padre directo de una organización.
   *
   * @param orgId - UUID de la organización hija
   * @returns Organización padre, o `undefined` si es la raíz o no se encuentra
   */
  const getParent = useCallback((orgId: string): Organization | undefined => {
    const org = organizations.find(o => o.id === orgId);
    if (!org?.parentId) return undefined;
    return organizations.find(o => o.id === org.parentId);
  }, [organizations]);

  /**
   * Retorna todos los ancestros de una organización, ordenados de raíz a padre inmediato.
   * Utiliza recorrido iterativo (no recursivo) para evitar stack overflow en jerarquías
   * profundas o con ciclos accidentales en datos corruptos.
   *
   * Ejemplo: getAncestors(Hospital.id) → [MINSAL, ServicioSalud]
   *
   * El `.reverse()` al final corrige el orden: la acumulación es de padre a raíz,
   * pero se expone de raíz a padre para facilitar la renderización de breadcrumbs.
   *
   * @param orgId - UUID de la organización
   * @returns Array de ancestros ordenados [raíz, ..., padre inmediato]
   */
  const getAncestors = useCallback((orgId: string): Organization[] => {
    const ancestors: Organization[] = [];
    let current = organizations.find(o => o.id === orgId);

    while (current?.parentId) {
      const parent = organizations.find(o => o.id === current!.parentId);
      if (parent) {
        ancestors.push(parent);
        current = parent;
      } else {
        break;
      }
    }

    // Invertir: acumulamos de hoja a raíz, exponemos de raíz a hoja
    return ancestors.reverse();
  }, [organizations]);

  /**
   * Retorna todos los descendientes de una organización en cualquier nivel inferior.
   * Utiliza BFS (Breadth-First Search) con cola explícita para garantizar que todos
   * los niveles son visitados antes de descender — el orden no es crítico aquí,
   * pero BFS es más predecible que DFS para depuración.
   *
   * Ejemplo: getDescendants(ServicioSalud.id) → [Hospital1, Hospital2, CESFAM1, ...]
   *
   * @param orgId - UUID de la organización raíz de la subjerarquía
   * @returns Todos los descendientes (hijos, nietos, etc.) sin el nodo raíz
   */
  const getDescendants = useCallback((orgId: string): Organization[] => {
    const descendants: Organization[] = [];
    const queue = [orgId];

    while (queue.length > 0) {
      const currentId = queue.shift()!;
      const children = organizations.filter(o => o.parentId === currentId);

      children.forEach(child => {
        descendants.push(child);
        // Encolar hijos para seguir el BFS en el siguiente nivel
        queue.push(child.id);
      });
    }

    return descendants;
  }, [organizations]);

  /**
   * Determina si el usuario autenticado puede acceder a los datos de una organización target.
   * Implementa el modelo de acceso jerárquico del MINSAL:
   *
   *   - Nivel 1 (MINSAL)    → acceso total a todas las organizaciones
   *   - Nivel 2 (SS/SEREMI) → acceso a sí mismo + todos sus descendientes
   *   - Nivel 3 (Hospital)  → acceso solo a sí mismo
   *
   * Esta función es el complemento en cliente de la función RLS en PostgreSQL
   * `can_user_access_organization()`, que aplica la misma lógica en las queries.
   *
   * @param targetOrgId - UUID de la organización a la que se intenta acceder
   * @returns `true` si el usuario puede ver datos de esa organización
   */
  const canAccessOrganization = useCallback((targetOrgId: string): boolean => {
    if (!currentOrganization) return false;

    // Acceso a la propia organización — siempre permitido
    if (currentOrganization.id === targetOrgId) return true;

    // MINSAL (nivel 1) tiene visibilidad total — es el único nodo sin padre
    if (currentOrganization.level === 1) return true;

    // Nivel 2 (SS/SEREMI/Autónomos) accede a sus descendientes directos e indirectos
    if (currentOrganization.level === 2) {
      const descendants = getDescendants(currentOrganization.id);
      return descendants.some(d => d.id === targetOrgId);
    }

    // Nivel 3 (Hospitales, CESFAM, etc.) solo ve sus propios datos
    return false;
  }, [currentOrganization, getDescendants]);

  // =====================================================
  // FUNCIONES DE FILTRADO
  // =====================================================

  /**
   * Filtra organizaciones por tipo funcional.
   * Útil para obtener solo hospitales, solo SS, etc.
   *
   * @param type - Tipo de organización según `OrganizationType`
   * @returns Array de organizaciones del tipo indicado
   */
  const getOrganizationsByType = useCallback((type: OrganizationType): Organization[] => {
    return organizations.filter(o => o.orgType === type);
  }, [organizations]);

  /**
   * Filtra organizaciones por región de Chile.
   * Las organizaciones nacionales (ej. Fonasa) tienen `regionCode` undefined y no aparecerán.
   *
   * @param region - Código de región ('RM', 'V', 'IX', etc.)
   * @returns Array de organizaciones ubicadas en esa región
   */
  const getOrganizationsByRegion = useCallback((region: ChileRegion): Organization[] => {
    return organizations.filter(o => o.regionCode === region);
  }, [organizations]);

  /**
   * Filtra organizaciones por nivel en la jerarquía.
   *
   * @param level - Nivel jerárquico (1=MINSAL, 2=SS/SEREMI, 3=Hospitales)
   * @returns Array de organizaciones en ese nivel
   */
  const getOrganizationsByLevel = useCallback((level: number): Organization[] => {
    return organizations.filter(o => o.level === level);
  }, [organizations]);

  // =====================================================
  // FUNCIONES DE MÉTRICAS
  // =====================================================

  /**
   * Busca las estadísticas de cumplimiento de una organización para un framework dado.
   * Retorna `undefined` si la vista materializada no tiene datos para esa combinación.
   *
   * @param orgId       - UUID de la organización
   * @param frameworkId - UUID del framework normativo (ISO 27002, LMCS, CIS, etc.)
   * @returns `OrganizationStats` si existe en la vista, `undefined` si no hay datos
   */
  const getOrgStatsByFramework = useCallback((
    orgId: string,
    frameworkId: string
  ): OrganizationStats | undefined => {
    return organizationStats.find(
      s => s.id === orgId && s.frameworkId === frameworkId
    );
  }, [organizationStats]);

  /**
   * Busca las estadísticas regionales para una combinación región + framework.
   *
   * @param regionCode  - Código de región chilena
   * @param frameworkId - UUID del framework normativo
   * @returns `RegionalStats` si existe, `undefined` si la vista no tiene datos para esa combinación
   */
  const getRegionalStatsByFramework = useCallback((
    regionCode: ChileRegion,
    frameworkId: string
  ): RegionalStats | undefined => {
    return regionalStats.find(
      r => r.regionCode === regionCode && r.frameworkId === frameworkId
    );
  }, [regionalStats]);

  // =====================================================
  // FUNCIONES DE REFRESH
  // =====================================================

  /**
   * Recarga la lista de organizaciones desde la BD.
   * Útil tras crear/desactivar una organización desde el panel SuperAdmin.
   */
  const refreshOrganizations = useCallback(async () => {
    await loadOrganizations();
  }, [loadOrganizations]);

  /**
   * Recarga las estadísticas desde las vistas materializadas.
   * Las vistas se refrescan en PostgreSQL con `SELECT refresh_compliance_views()`.
   * Este método actualiza el estado React con los datos más recientes.
   *
   * @param frameworkId - Filtrar por framework específico (undefined = todos)
   */
  const refreshStats = useCallback(async (frameworkId?: string) => {
    await loadStats(frameworkId);
  }, [loadStats]);

  // =====================================================
  // VALORES DERIVADOS
  // =====================================================

  /**
   * Hijos directos de la organización actual.
   * Calculado en cada render — no almacenado en estado para evitar sincronización manual.
   * Para MINSAL: todos los SS + SEREMIS + Autónomos.
   * Para un SS: todos sus hospitales y CESFAM.
   */
  const childOrganizations = currentOrganization
    ? getChildren(currentOrganization.id)
    : [];

  /**
   * Organización padre de la actual.
   * Null para MINSAL (raíz de la jerarquía).
   * Para un hospital: el Servicio de Salud al que pertenece.
   */
  const parentOrganization = currentOrganization
    ? getParent(currentOrganization.id) || null
    : null;

  // =====================================================
  // VALOR DEL CONTEXTO
  // =====================================================

  const value: OrganizationContextType = {
    // Estado
    currentOrganization,
    organizations,
    organizationTree,
    childOrganizations,
    parentOrganization,
    isLoading,
    error,
    isDemoMode,

    // Métricas
    organizationStats,
    regionalStats,
    nationalStats,
    criticalAlerts,

    // Funciones de navegación
    getOrganization,
    getChildren,
    getParent,
    getAncestors,
    getDescendants,
    canAccessOrganization,

    // Funciones de datos
    refreshOrganizations,
    refreshStats,
    getOrgStatsByFramework,
    getRegionalStatsByFramework,

    // Funciones de filtrado
    getOrganizationsByType,
    getOrganizationsByRegion,
    getOrganizationsByLevel,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

// =====================================================
// HOOK PERSONALIZADO
// =====================================================

/**
 * Hook para consumir el contexto de organizaciones en componentes funcionales.
 * Lanza un error descriptivo si se usa fuera de `OrganizationProvider` para
 * detectar errores de configuración en tiempo de desarrollo.
 *
 * @returns Valor completo del `OrganizationContextType`
 * @throws Error si el hook se usa fuera del árbol del provider
 *
 * @example
 * const { currentOrganization, getChildren, canAccessOrganization } = useOrganization();
 */
export function useOrganization(): OrganizationContextType {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization debe usarse dentro de un OrganizationProvider');
  }
  return context;
}

export default OrganizationContext;
